/**
 * Notification Service - Handles all notification-related operations
 * Including creation, marking as read, and real-time updates
 */

import { database } from './firebase';
import {
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  off,
  serverTimestamp,
  push,
  query,
  orderByChild,
  limitToLast
} from 'firebase/database';

/**
 * Notification Types
 */
export const NOTIFICATION_TYPES = {
  ROOM_INVITE: 'room_invite',
  PLAYER_JOINED: 'player_joined',
  PLAYER_READY: 'player_ready',
  GAME_SELECTED: 'game_selected',
  GAME_STARTED: 'game_started',
  YOUR_TURN: 'your_turn',
  GAME_ENDED: 'game_ended',
  FRIEND_REQUEST: 'friend_request',
  MESSAGE_RECEIVED: 'message_received',
  SYSTEM: 'system'
};

/**
 * Creates a notification for a user
 *
 * @param {string} userId - ID of the user receiving the notification
 * @param {Object} notificationData - Notification details
 * @param {string} notificationData.type - Type of notification (from NOTIFICATION_TYPES)
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message
 * @param {Object} notificationData.data - Additional data (roomCode, gameId, etc.)
 * @param {string} notificationData.actionUrl - Optional URL to navigate to
 * @returns {Promise<string>} The notification ID
 */
export const createNotification = async (userId, notificationData) => {
  try {
    const notificationsRef = ref(database, `notifications/${userId}`);
    const newNotificationRef = push(notificationsRef);

    const notification = {
      id: newNotificationRef.key,
      type: notificationData.type,
      title: notificationData.title,
      message: notificationData.message,
      data: notificationData.data || {},
      actionUrl: notificationData.actionUrl || null,
      read: false,
      createdAt: serverTimestamp(),
      timestamp: Date.now()
    };

    await set(newNotificationRef, notification);
    return newNotificationRef.key;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Marks a notification as read
 *
 * @param {string} userId - ID of the user
 * @param {string} notificationId - ID of the notification
 * @returns {Promise<void>}
 */
export const markAsRead = async (userId, notificationId) => {
  try {
    const notificationRef = ref(database, `notifications/${userId}/${notificationId}`);
    await update(notificationRef, {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Marks all notifications as read for a user
 *
 * @param {string} userId - ID of the user
 * @returns {Promise<void>}
 */
export const markAllAsRead = async (userId) => {
  try {
    const notificationsRef = ref(database, `notifications/${userId}`);
    const snapshot = await get(notificationsRef);

    if (snapshot.exists()) {
      const updates = {};
      snapshot.forEach((childSnapshot) => {
        if (!childSnapshot.val().read) {
          updates[`${childSnapshot.key}/read`] = true;
          updates[`${childSnapshot.key}/readAt`] = serverTimestamp();
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(notificationsRef, updates);
      }
    }
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Deletes a notification
 *
 * @param {string} userId - ID of the user
 * @param {string} notificationId - ID of the notification
 * @returns {Promise<void>}
 */
export const deleteNotification = async (userId, notificationId) => {
  try {
    const notificationRef = ref(database, `notifications/${userId}/${notificationId}`);
    await remove(notificationRef);
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

/**
 * Deletes all notifications for a user
 *
 * @param {string} userId - ID of the user
 * @returns {Promise<void>}
 */
export const deleteAllNotifications = async (userId) => {
  try {
    const notificationsRef = ref(database, `notifications/${userId}`);
    await remove(notificationsRef);
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
};

/**
 * Gets all notifications for a user
 *
 * @param {string} userId - ID of the user
 * @param {number} limit - Maximum number of notifications to retrieve (default: 50)
 * @returns {Promise<Array>} Array of notifications
 */
export const getNotifications = async (userId, limit = 50) => {
  try {
    const notificationsRef = ref(database, `notifications/${userId}`);
    const notificationsQuery = query(
      notificationsRef,
      orderByChild('timestamp'),
      limitToLast(limit)
    );

    const snapshot = await get(notificationsQuery);

    if (!snapshot.exists()) {
      return [];
    }

    const notifications = [];
    snapshot.forEach((childSnapshot) => {
      notifications.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Return in reverse chronological order (newest first)
    return notifications.reverse();
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

/**
 * Gets the count of unread notifications
 *
 * @param {string} userId - ID of the user
 * @returns {Promise<number>} Number of unread notifications
 */
export const getUnreadCount = async (userId) => {
  try {
    const notificationsRef = ref(database, `notifications/${userId}`);
    const snapshot = await get(notificationsRef);

    if (!snapshot.exists()) {
      return 0;
    }

    let count = 0;
    snapshot.forEach((childSnapshot) => {
      if (!childSnapshot.val().read) {
        count++;
      }
    });

    return count;
  } catch (error) {
    console.error('Error getting unread count:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time notification updates
 *
 * @param {string} userId - ID of the user
 * @param {Function} callback - Function to call when notifications change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToNotifications = (userId, callback) => {
  const notificationsRef = ref(database, `notifications/${userId}`);

  const unsubscribe = onValue(notificationsRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback([]);
      return;
    }

    const notifications = [];
    snapshot.forEach((childSnapshot) => {
      notifications.push({
        id: childSnapshot.key,
        ...childSnapshot.val()
      });
    });

    // Sort by timestamp (newest first)
    notifications.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    callback(notifications);
  });

  return () => off(notificationsRef, 'value', unsubscribe);
};

/**
 * Helper function to create a player joined notification
 *
 * @param {string} hostId - ID of the room host
 * @param {string} guestName - Display name of the guest who joined
 * @param {string} roomCode - Room code
 * @returns {Promise<string>} The notification ID
 */
export const notifyPlayerJoined = async (hostId, guestName, roomCode) => {
  return createNotification(hostId, {
    type: NOTIFICATION_TYPES.PLAYER_JOINED,
    title: 'Player Joined',
    message: `${guestName} joined your room`,
    data: { roomCode },
    actionUrl: `/room/${roomCode}`
  });
};

/**
 * Helper function to create a player ready notification
 *
 * @param {string} recipientId - ID of the user to notify
 * @param {string} playerName - Display name of the player who is ready
 * @param {string} roomCode - Room code
 * @returns {Promise<string>} The notification ID
 */
export const notifyPlayerReady = async (recipientId, playerName, roomCode) => {
  return createNotification(recipientId, {
    type: NOTIFICATION_TYPES.PLAYER_READY,
    title: 'Player Ready',
    message: `${playerName} is ready to play!`,
    data: { roomCode },
    actionUrl: `/room/${roomCode}`
  });
};

/**
 * Helper function to create a game selected notification
 *
 * @param {string} recipientId - ID of the user to notify
 * @param {string} gameName - Name of the selected game
 * @param {string} roomCode - Room code
 * @returns {Promise<string>} The notification ID
 */
export const notifyGameSelected = async (recipientId, gameName, roomCode) => {
  return createNotification(recipientId, {
    type: NOTIFICATION_TYPES.GAME_SELECTED,
    title: 'Game Selected',
    message: `Your opponent selected ${gameName}`,
    data: { roomCode, gameName },
    actionUrl: `/room/${roomCode}`
  });
};

/**
 * Helper function to create a game started notification
 *
 * @param {string} recipientId - ID of the user to notify
 * @param {string} gameName - Name of the game
 * @param {string} roomCode - Room code
 * @returns {Promise<string>} The notification ID
 */
export const notifyGameStarted = async (recipientId, gameName, roomCode) => {
  return createNotification(recipientId, {
    type: NOTIFICATION_TYPES.GAME_STARTED,
    title: 'Game Started',
    message: `${gameName} has started!`,
    data: { roomCode, gameName },
    actionUrl: `/room/${roomCode}`
  });
};

/**
 * Helper function to create a turn notification
 *
 * @param {string} recipientId - ID of the user to notify
 * @param {string} gameName - Name of the game
 * @param {string} roomCode - Room code
 * @returns {Promise<string>} The notification ID
 */
export const notifyYourTurn = async (recipientId, gameName, roomCode) => {
  return createNotification(recipientId, {
    type: NOTIFICATION_TYPES.YOUR_TURN,
    title: 'Your Turn',
    message: `It's your turn in ${gameName}!`,
    data: { roomCode, gameName },
    actionUrl: `/room/${roomCode}`
  });
};

/**
 * Helper function to create a game ended notification
 *
 * @param {string} recipientId - ID of the user to notify
 * @param {string} result - 'won', 'lost', or 'draw'
 * @param {string} gameName - Name of the game
 * @param {string} roomCode - Room code
 * @returns {Promise<string>} The notification ID
 */
export const notifyGameEnded = async (recipientId, result, gameName, roomCode) => {
  const messages = {
    won: `You won the ${gameName} game!`,
    lost: `You lost the ${gameName} game`,
    draw: `The ${gameName} game ended in a draw`
  };

  return createNotification(recipientId, {
    type: NOTIFICATION_TYPES.GAME_ENDED,
    title: result === 'won' ? 'Victory!' : result === 'draw' ? 'Draw' : 'Game Over',
    message: messages[result],
    data: { roomCode, gameName, result },
    actionUrl: `/room/${roomCode}`
  });
};
